import React from 'react';
import useAuth from 'Hooks/useAuth';
import { Location } from 'history';
import { Redirect, Route, RouteProps } from 'react-router-dom';
import urls from 'Source/urls';
import useRouter from 'Hooks/useRouter';
import { MCP_AUTH_STATE_KEY, PANTHER_SESSION_REDIRECT_KEY } from 'Source/constants';
import storage from 'Helpers/storage';

interface GuardedRouteProps extends RouteProps {
  limitAccessTo: 'anonymous' | 'authenticated';
}

/**
 * Makes sure to render a Route only if the guarded restriction is satisfied. Else we just render
 * a redirect to a URL according to the restriction type
 */
const GuardedRoute: React.FC<GuardedRouteProps> = ({ limitAccessTo, ...rest }) => {
  const { isAuthenticated } = useAuth();
  const { location } = useRouter<Record<string, string>, { referrer: Location }>();

  // Remote-MCP OAuth handoff: when the REST `/oauth/authorize`
  // endpoint redirects the user to the sign-in page, it passes
  // `mcp_auth=true&state=<encrypted-blob>` in the query string. Capture the
  // state into session storage immediately on render — not in an effect —
  // because an already-authenticated user is redirected away from this
  // component synchronously, before any useEffect could run. The companion
  // `flow_id` cookie was already set on the same response by the REST
  // handler and is readable from `/mcp-approval` via document.cookie.
  const mcpWritten = React.useRef(false);
  const searchParams = new URLSearchParams(location.search);
  const mcpAuthParam = searchParams.get('mcp_auth');
  const mcpStateParam = searchParams.get('state');
  if (!mcpWritten.current && mcpAuthParam === 'true' && mcpStateParam) {
    storage.session.write(MCP_AUTH_STATE_KEY, mcpStateParam);
    mcpWritten.current = true;
  }

  if (limitAccessTo === 'anonymous' && !isAuthenticated) {
    const reauthPath = storage.session.read(PANTHER_SESSION_REDIRECT_KEY);
    if (reauthPath) {
      storage.session.delete(PANTHER_SESSION_REDIRECT_KEY);
      const redirectData = {
        pathname: urls.account.auth.signIn(),
        state: { ...location.state, referrer: { pathname: reauthPath } },
      };
      return <Redirect to={redirectData} push={false} />;
    }
    return <Route {...rest} />;
  }

  if (limitAccessTo === 'authenticated' && isAuthenticated) {
    return <Route {...rest} />;
  }

  // Ok so, the following lines are not as simple as they appear.
  let redirectData: { pathname: string; state: { referrer: Location } };

  // This one simply redirects the user to the SignIn page and adds a referrer page to return
  // back to when the user becomes authenticated. It's as simple as it appears. User went to access
  // a protected page and the page went "buddy gimme some creds first"
  if (limitAccessTo === 'authenticated') {
    redirectData = {
      pathname: urls.account.auth.signIn(),
      state: { ...location.state, referrer: location },
    };
    // This one means that an authenticated user is trying to access an anonymous only page. What we
    // do is redirect them to a referrer page if it existed or just the base page. Now why the
    // referrer? Because when the user signs in, he's still in the SignIn page which suddenly
    // denied access to him because the "guarded-route" said "hey buddy you are now authenticated,
    // you can't be accessing the SignIn page". The user said though "but I literally just signed-
    // in, where else could I be?!". Because both of these people are right, we redirect the user
    // to the referrer *if it exists* (a.k.a. if he just signed in) or the home page if he's just
    // plain stupid and manually went to the SignIn page through the URL bar (in which case no
    // referrer will exist)
  } else {
    // Remote-MCP priority override: an authenticated user hitting the
    // SignIn page while an MCP auth flow is pending must be routed to the
    // approval screen, regardless of any prior referrer. This is what
    // closes the loop between `/oauth/authorize` → sign-in → approval.
    const mcpAuthPending = storage.session.read(MCP_AUTH_STATE_KEY);
    if (mcpAuthPending) {
      redirectData = {
        pathname: urls.account.auth.mcpApproval(),
        state: undefined,
      };
    } else {
      redirectData = {
        ...location.state?.referrer,
        pathname: location?.state?.referrer?.pathname || '/',
        state: undefined,
      };
    }
  }

  return <Redirect to={redirectData} push={false} />;
};

export default GuardedRoute;
